import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LogOut, LayoutGrid, Users, ClipboardList, MessageSquare,
  Plus, CheckCircle2, Circle, AlertTriangle, Calendar,
  ChevronDown, ChevronUp, Star, ThumbsUp, ThumbsDown,
  Trash2, RefreshCw, TrendingUp,
} from "lucide-react";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const TABS = [
  { id: "dashboard",  icon: LayoutGrid },
  { id: "templates",  icon: ClipboardList },
  { id: "active",     icon: Users },
  { id: "interviews", icon: MessageSquare },
];

const EXIT_REASON_KEYS = ["resignation", "termination", "retirement", "end_of_contract", "mutual_agreement", "redundancy"];

const CATEGORY_COLORS = {
  it_access:          "text-red-400 bg-red-500/10 border-red-500/20",
  equipment:          "text-orange-400 bg-orange-500/10 border-orange-500/20",
  knowledge_transfer: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  hr_admin:           "text-blue-400 bg-blue-500/10 border-blue-500/20",
  finance:            "text-green-400 bg-green-500/10 border-green-500/20",
  legal:              "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  general:            "text-gray-400 bg-gray-500/10 border-gray-500/20",
};

const StarRating = ({ value, onChange, readonly }) => (
  <div className="flex gap-1">
    {[1,2,3,4,5].map(n => (
      <button key={n} type={readonly ? "button" : "button"}
        onClick={() => !readonly && onChange && onChange(n)}
        className={`${readonly ? "cursor-default" : "cursor-pointer"} transition-colors`}>
        <Star className={`w-5 h-5 ${n <= (value || 0) ? "text-yellow-400 fill-yellow-400" : "text-[var(--lm-border)]"}`} />
      </button>
    ))}
  </div>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("v1/offboarding/dashboard").then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-center py-20 text-[var(--lm-muted)]">{t("offboardingHub.loading")}</div>;

  const kpis = [
    { label: t("offboardingHub.dashboard.kpiInProgress"),    value: data.in_progress,          icon: Users,        color: "blue" },
    { label: t("offboardingHub.dashboard.kpiCompleted"),     value: data.completed,            icon: CheckCircle2, color: "green" },
    { label: t("offboardingHub.dashboard.kpiPendingTasks"),  value: data.pending_tasks,        icon: ClipboardList,color: "yellow" },
    { label: t("offboardingHub.dashboard.kpiAvgSatisfaction"), value: data.avg_satisfaction != null ? `${data.avg_satisfaction}/5` : "—", icon: Star, color: "purple" },
  ];
  const colorBg   = { blue: "bg-blue-500/10",   green: "bg-green-500/10",   yellow: "bg-yellow-500/10",  purple: "bg-purple-500/10" };
  const colorText = { blue: "text-blue-400",    green: "text-green-400",    yellow: "text-yellow-400",   purple: "text-purple-400" };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} className={`p-4 rounded-xl border border-[var(--lm-border)] ${colorBg[k.color]}`}>
            <k.icon className={`w-5 h-5 mb-2 ${colorText[k.color]}`} />
            <p className="text-2xl font-bold text-[var(--lm-text)]">{k.value ?? "0"}</p>
            <p className="text-xs text-[var(--lm-muted)] mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {data.recommendation_rate != null && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <h3 className="font-semibold text-[var(--lm-text)] mb-3">{t("offboardingHub.dashboard.recommendationTitle")}</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-3 bg-[var(--lm-bg)] rounded-full overflow-hidden">
              <div className="h-3 bg-green-500 rounded-full" style={{ width: `${data.recommendation_rate}%` }} />
            </div>
            <span className="text-lg font-bold text-green-400">{data.recommendation_rate}%</span>
          </div>
        </div>
      )}

      {Object.keys(data.exit_reason_breakdown || {}).length > 0 && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <h3 className="font-semibold text-[var(--lm-text)] mb-4">{t("offboardingHub.dashboard.exitReasonsTitle")}</h3>
          <div className="space-y-2">
            {Object.entries(data.exit_reason_breakdown).map(([reason, count]) => {
              const total = Object.values(data.exit_reason_breakdown).reduce((a, b) => a + b, 0);
              const pct = Math.round(count / total * 100);
              return (
                <div key={reason} className="flex items-center gap-3">
                  <span className="text-sm text-[var(--lm-muted)] w-48 truncate">{t(`offboardingHub.exitReason.${reason}`, { defaultValue: reason })}</span>
                  <div className="flex-1 h-2 bg-[var(--lm-bg)] rounded-full overflow-hidden">
                    <div className="h-2 bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-[var(--lm-muted)] w-10 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.upcoming_last_days?.length > 0 && (
        <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
          <h3 className="font-semibold text-[var(--lm-text)] mb-4">{t("offboardingHub.dashboard.upcomingTitle")}</h3>
          <div className="space-y-2">
            {data.upcoming_last_days.map(o => (
              <div key={o.id} className="flex items-center justify-between p-3 rounded-lg bg-[var(--lm-bg)]">
                <div>
                  <p className="font-medium text-sm text-[var(--lm-text)]">{o.employee_name}</p>
                  <p className="text-xs text-[var(--lm-muted)]">{t(`offboardingHub.exitReason.${o.exit_reason}`, { defaultValue: o.exit_reason })}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-[var(--lm-text)]">{o.last_day}</p>
                  <p className={`text-xs ${o.days_left <= 7 ? "text-red-400" : "text-[var(--lm-muted)]"}`}>
                    {o.days_left === 0 ? t("offboardingHub.dashboard.today") : o.days_left < 0 ? t("offboardingHub.dashboard.daysPassed", { count: Math.abs(o.days_left) }) : t("offboardingHub.dashboard.daysLeft", { count: o.days_left })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Templates Tab ──────────────────────────────────────────────────────────────

function TemplatesTab() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", is_default: false });

  const load = useCallback(() => apiClient.get("v1/offboarding/templates").then(r => setTemplates(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error(t("offboardingHub.templates.nameRequired")); return; }
    await apiClient.post("v1/offboarding/templates", form);
    toast.success(t("offboardingHub.templates.created")); setShowModal(false); setForm({ name: "", description: "", is_default: false }); load();
  };

  const del = async (id) => {
    if (!window.confirm(t("offboardingHub.templates.confirmDelete"))) return;
    await apiClient.delete(`v1/offboarding/templates/${id}`);
    toast.success(t("offboardingHub.templates.deleted")); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[var(--lm-muted)]">{t("offboardingHub.templates.count", { count: templates.length })}</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("offboardingHub.templates.new")}
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("offboardingHub.templates.emptyTitle")}</p>
          <p className="text-xs mt-1">{t("offboardingHub.templates.emptyHint")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(tpl => (
            <div key={tpl.id} className="rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] overflow-hidden">
              <div className="p-4 flex items-center justify-between gap-4 cursor-pointer" onClick={() => setExpanded(expanded === tpl.id ? null : tpl.id)}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-[var(--lm-text)]">{tpl.name}</span>
                    {tpl.is_default && <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{t("offboardingHub.templates.defaultBadge")}</span>}
                  </div>
                  <p className="text-xs text-[var(--lm-muted)] mt-0.5">{t("offboardingHub.templates.taskCount", { count: tpl.task_count })}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={e => { e.stopPropagation(); del(tpl.id); }} className="p-1.5 rounded text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expanded === tpl.id ? <ChevronUp className="w-4 h-4 text-[var(--lm-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--lm-muted)]" />}
                </div>
              </div>
              {expanded === tpl.id && tpl.tasks?.length > 0 && (
                <div className="border-t border-[var(--lm-border)] p-4 bg-[var(--lm-bg)]">
                  <div className="space-y-1.5">
                    {tpl.tasks.map((task, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded border ${CATEGORY_COLORS[task.category] || CATEGORY_COLORS.general}`}>
                          {task.owner_role || "—"}
                        </span>
                        <span className="text-sm text-[var(--lm-text)]">{task.title}</span>
                        {task.days_before_last > 0 && (
                          <span className="text-xs text-[var(--lm-muted)] ml-auto">J-{task.days_before_last}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
              <h2 className="font-semibold text-[var(--lm-text)]">{t("offboardingHub.templates.modalTitle")}</h2>
              <button onClick={() => setShowModal(false)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <form onSubmit={create} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.templates.name")}</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={t("offboardingHub.templates.namePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.templates.description")}</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm resize-none" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_default} onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))} />
                <span className="text-sm text-[var(--lm-text)]">{t("offboardingHub.templates.useAsDefault")}</span>
              </label>
              <p className="text-xs text-[var(--lm-muted)]">{t("offboardingHub.templates.prefillNote")}</p>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("common.cancel")}</button>
                <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">{t("offboardingHub.templates.create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Active Offboardings Tab ────────────────────────────────────────────────────

function ActiveTab() {
  const { t } = useTranslation();
  const [offboardings, setOffboardings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ employee_id: "", last_day: "", exit_reason: "resignation", template_id: "", notes: "" });
  const [statusFilter, setStatusFilter] = useState("in_progress");

  const load = useCallback(() => {
    apiClient.get(`v1/offboarding${statusFilter ? `?status=${statusFilter}` : ""}`).then(r => setOffboardings(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [statusFilter]);

  useEffect(() => {
    load();
    apiClient.get("v1/employees/?limit=200").then(r => setEmployees(Array.isArray(r.data) ? r.data : (r.data?.employees || [])));
    apiClient.get("v1/offboarding/templates").then(r => setTemplates(Array.isArray(r.data) ? r.data : []));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.last_day) { toast.error(t("offboardingHub.active.requiredFields")); return; }
    const payload = { ...form, employee_id: parseInt(form.employee_id) };
    payload.template_id = form.template_id ? parseInt(form.template_id) : null;
    try {
      await apiClient.post("v1/offboarding", payload);
      toast.success(t("offboardingHub.active.launched")); setShowCreate(false); load();
    } catch { toast.error(t("common.error")); }
  };

  const toggleTask = async (taskId) => {
    await apiClient.post(`v1/offboarding/tasks/${taskId}/toggle`);
    load();
  };

  const complete = async (id) => {
    if (!window.confirm(t("offboardingHub.active.confirmComplete"))) return;
    await apiClient.post(`v1/offboarding/${id}/complete`);
    toast.success(t("offboardingHub.active.completed")); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          {[["in_progress", t("offboardingHub.active.filterInProgress")], ["completed", t("offboardingHub.active.filterCompleted")], ["", t("offboardingHub.active.filterAll")]].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              className={`px-3 py-1.5 text-xs rounded-lg border ${statusFilter === v ? "bg-blue-600 text-white border-blue-600" : "bg-[var(--lm-bg)] text-[var(--lm-muted)] border-[var(--lm-border)] hover:text-[var(--lm-text)]"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("offboardingHub.active.launch")}
        </button>
      </div>

      {offboardings.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <LogOut className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("offboardingHub.active.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {offboardings.map(o => (
            <div key={o.id} className="rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[var(--lm-text)]">{o.employee_name}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">
                        {o.exit_reason_label}
                      </span>
                      {o.days_left != null && o.days_left <= 7 && o.status === "in_progress" && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {o.days_left <= 0 ? t("offboardingHub.active.lastDayPassed") : t("offboardingHub.active.daysLeft", { count: o.days_left })}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--lm-muted)] mt-1">{t("offboardingHub.active.lastDayLabel", { date: o.last_day })}</p>

                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex-1 max-w-xs h-1.5 bg-[var(--lm-bg)] rounded-full overflow-hidden">
                        <div className={`h-1.5 rounded-full ${o.completion_pct === 100 ? "bg-green-500" : "bg-blue-500"}`}
                          style={{ width: `${o.completion_pct}%` }} />
                      </div>
                      <span className="text-xs text-[var(--lm-muted)]">
                        {t("offboardingHub.active.tasksCount", { completed: o.tasks_completed, total: o.tasks_total })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {o.status === "in_progress" && o.completion_pct === 100 && (
                      <button onClick={() => complete(o.id)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-green-600/10 text-green-400 hover:bg-green-600/20 border border-green-500/20">
                        {t("offboardingHub.active.close")}
                      </button>
                    )}
                    <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="p-1.5 text-[var(--lm-muted)]">
                      {expanded === o.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {expanded === o.id && (
                <div className="border-t border-[var(--lm-border)] p-4 bg-[var(--lm-bg)]">
                  {Object.entries(
                    (o.tasks || []).reduce((acc, t) => {
                      const cat = t.category || "general";
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(t);
                      return acc;
                    }, {})
                  ).map(([cat, tasks]) => (
                    <div key={cat} className="mb-4">
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 px-2 py-0.5 rounded inline-block border ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.general}`}>
                        {cat.replace("_", " ")}
                      </p>
                      <div className="space-y-1.5">
                        {tasks.map(task => (
                          <div key={task.id} className="flex items-center gap-3">
                            <button onClick={() => toggleTask(task.id)} className="flex-shrink-0">
                              {task.is_completed
                                ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                                : <Circle className="w-4 h-4 text-[var(--lm-muted)]" />
                              }
                            </button>
                            <span className={`text-sm flex-1 ${task.is_completed ? "line-through text-[var(--lm-muted)]" : "text-[var(--lm-text)]"}`}>
                              {task.title}
                            </span>
                            {task.due_date && !task.is_completed && (
                              <span className="text-xs text-[var(--lm-muted)]">{task.due_date}</span>
                            )}
                            {task.owner_role && (
                              <span className="text-xs text-[var(--lm-muted)] hidden sm:block">{task.owner_role}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
              <h2 className="font-semibold text-[var(--lm-text)]">{t("offboardingHub.active.modalTitle")}</h2>
              <button onClick={() => setShowCreate(false)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <form onSubmit={create} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.active.employee")}</label>
                <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                  <option value="">{t("offboardingHub.active.selectPlaceholder")}</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.active.lastDay")}</label>
                  <input type="date" value={form.last_day} onChange={e => setForm(f => ({ ...f, last_day: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.active.reason")}</label>
                  <select value={form.exit_reason} onChange={e => setForm(f => ({ ...f, exit_reason: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                    {EXIT_REASON_KEYS.map((v) => <option key={v} value={v}>{t(`offboardingHub.exitReason.${v}`)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.active.template")}</label>
                <select value={form.template_id} onChange={e => setForm(f => ({ ...f, template_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm">
                  <option value="">{t("offboardingHub.active.defaultTemplate")}</option>
                  {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.active.notes")}</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm resize-none" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("common.cancel")}</button>
                <button type="submit" className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">{t("offboardingHub.active.launchCta")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Exit Interviews Tab ────────────────────────────────────────────────────────

function InterviewsTab() {
  const { t } = useTranslation();
  const [offboardings, setOffboardings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [interview, setInterview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    apiClient.get("v1/offboarding").then(r => setOffboardings(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const loadInterview = async (offboardingId) => {
    setSelected(offboardingId);
    setInterview(null);
    setEditing(false);
    try {
      const { data } = await apiClient.get(`v1/offboarding/${offboardingId}/interview`);
      setInterview(data);
      setForm(data);
    } catch {
      setInterview(null);
      setForm({});
    }
  };

  const save = async () => {
    const { data } = await apiClient.put(`v1/offboarding/${selected}/interview`, form);
    setInterview(data);
    setEditing(false);
    toast.success(t("offboardingHub.interviews.saved"));
  };

  const f = (field) => form[field];
  const sf = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="space-y-2">
        <p className="text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider mb-3">{t("offboardingHub.interviews.selectEmployee")}</p>
        {offboardings.length === 0 ? (
          <p className="text-sm text-[var(--lm-muted)]">{t("offboardingHub.interviews.empty")}</p>
        ) : offboardings.map(o => (
          <button key={o.id} onClick={() => loadInterview(o.id)}
            className={`w-full text-left p-3 rounded-lg border transition-colors ${selected === o.id ? "border-blue-500 bg-blue-500/10" : "border-[var(--lm-border)] bg-[var(--lm-s1)] hover:bg-[var(--lm-bg)]"}`}>
            <p className="font-medium text-sm text-[var(--lm-text)]">{o.employee_name}</p>
            <p className="text-xs text-[var(--lm-muted)]">{o.exit_reason_label} · {o.last_day}</p>
            {o.has_exit_interview && <p className="text-xs text-green-400 mt-0.5">{t("offboardingHub.interviews.interviewSaved")}</p>}
          </button>
        ))}
      </div>

      <div className="lg:col-span-2">
        {!selected ? (
          <div className="text-center py-20 text-[var(--lm-muted)]">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>{t("offboardingHub.interviews.prompt")}</p>
          </div>
        ) : (
          <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[var(--lm-text)]">{t("offboardingHub.interviews.title")}</h3>
              {!editing ? (
                <button onClick={() => { setEditing(true); if (!interview) setForm({}); }}
                  className="text-xs px-3 py-1.5 rounded-lg bg-blue-600/10 text-blue-400 hover:bg-blue-600/20 border border-blue-500/20">
                  {interview ? t("offboardingHub.interviews.edit") : t("offboardingHub.interviews.enter")}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setEditing(false)} className="text-xs px-3 py-1.5 rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] border border-[var(--lm-border)]">{t("common.cancel")}</button>
                  <button onClick={save} className="text-xs px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white">{t("offboardingHub.interviews.save")}</button>
                </div>
              )}
            </div>

            {editing && (
              <div>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("offboardingHub.interviews.interviewDate")}</label>
                <input type="date" value={f("interview_date") || ""} onChange={e => sf("interview_date", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
              </div>
            )}

            {[
              ["overall_satisfaction",  t("offboardingHub.interviews.overallSatisfaction")],
              ["manager_satisfaction",  t("offboardingHub.interviews.managerSatisfaction")],
              ["culture_satisfaction",  t("offboardingHub.interviews.cultureSatisfaction")],
              ["growth_satisfaction",   t("offboardingHub.interviews.growthSatisfaction")],
            ].map(([field, label]) => (
              <div key={field}>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-2">{label}</label>
                <StarRating value={f(field)} onChange={v => editing && sf(field, v)} readonly={!editing} />
              </div>
            ))}

            {[
              ["main_reason",          t("offboardingHub.interviews.mainReason")],
              ["what_worked",          t("offboardingHub.interviews.whatWorked")],
              ["what_improve",         t("offboardingHub.interviews.whatImprove")],
              ["additional_comments",  t("offboardingHub.interviews.additionalComments")],
            ].map(([field, label]) => (
              <div key={field}>
                <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{label}</label>
                {editing ? (
                  <textarea value={f(field) || ""} onChange={e => sf(field, e.target.value)}
                    rows={3} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm resize-none" />
                ) : (
                  <p className="text-sm text-[var(--lm-text)] bg-[var(--lm-bg)] p-3 rounded-lg min-h-[60px]">
                    {f(field) || <span className="text-[var(--lm-muted)] italic">{t("offboardingHub.interviews.notProvided")}</span>}
                  </p>
                )}
              </div>
            ))}

            <div className="grid grid-cols-2 gap-4">
              {[
                ["would_recommend", t("offboardingHub.interviews.wouldRecommend")],
                ["would_return",    t("offboardingHub.interviews.wouldReturn")],
              ].map(([field, label]) => (
                <div key={field}>
                  <label className="block text-xs font-medium text-[var(--lm-muted)] mb-2">{label}</label>
                  {editing ? (
                    <div className="flex gap-3">
                      {[true, false].map(v => (
                        <button key={String(v)} type="button" onClick={() => sf(field, v)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm ${f(field) === v ? (v ? "bg-green-600/20 text-green-400 border-green-500/40" : "bg-red-600/20 text-red-400 border-red-500/40") : "bg-[var(--lm-bg)] text-[var(--lm-muted)] border-[var(--lm-border)]"}`}>
                          {v ? <ThumbsUp className="w-3.5 h-3.5" /> : <ThumbsDown className="w-3.5 h-3.5" />}
                          {v ? t("offboardingHub.interviews.yes") : t("offboardingHub.interviews.no")}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className={`flex items-center gap-1.5 text-sm ${f(field) === true ? "text-green-400" : f(field) === false ? "text-red-400" : "text-[var(--lm-muted)] italic"}`}>
                      {f(field) === true ? <ThumbsUp className="w-4 h-4" /> : f(field) === false ? <ThumbsDown className="w-4 h-4" /> : null}
                      {f(field) === true ? t("offboardingHub.interviews.yes") : f(field) === false ? t("offboardingHub.interviews.no") : t("offboardingHub.interviews.notProvided")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Hub ───────────────────────────────────────────────────────────────────────

export default function OffboardingHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderTab = () => {
    switch (activeTab) {
      case "dashboard":  return <DashboardTab />;
      case "templates":  return <TemplatesTab />;
      case "active":     return <ActiveTab />;
      case "interviews": return <InterviewsTab />;
      default:           return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20">
          <LogOut className="w-6 h-6 text-red-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--lm-text)]">{t("offboardingHub.title")}</h1>
          <p className="text-sm text-[var(--lm-muted)]">{t("offboardingHub.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--lm-border)] overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id ? "border-red-500 text-red-400" : "border-transparent text-[var(--lm-muted)] hover:text-[var(--lm-text)]"
            }`}>
            <tab.icon className="w-4 h-4" />
            {t(`offboardingHub.tabs.${tab.id}`)}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
}
